import React, { useState, useEffect } from "react";
import { dataService } from "../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, TextField } from "@mui/material";
import { createUserWithEmailAndPassword, getAuth, sendPasswordResetEmail, sendSignInLinkToEmail } from "firebase/auth";
// import admin from "firebase-admin";
import { X, Check, Infinity } from "lucide-react";
import { crudTypes, subscriptionTiers } from "../../../../Config/constants";
import AccountCategoryDropdown from "../../../Shared/Dropdowns/AccountCategoryDropdown";
import CustomCheckbox from "../../../Shared/Checkbox";
import Button from "../../../Shared/Buttons/Button";

export default function CreateAccountDialog({ dialogOpen, closeDialog, subscriberToEdit, ...props }) {
  const isEdit = !!subscriberToEdit;
  const classes = useStyles();
  const auth = getAuth();

  const [generalError, setGeneralError] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);

  const [name, setName] = useState(null);
  const [nameError, setNameError] = useState(null);

  const [administrator, setAdministrator] = useState(null);
  const [administratorError, setAdministratorError] = useState(null);

  const [administratorFirstName, setAdministratorFirstName] = useState(null);
  const [administratorFirstNameError, setAdministratorFirstNameError] = useState(null);

  const [administratorLastName, setAdministratorLastName] = useState(null);
  const [administratorLastNameError, setAdministratorLastNameError] = useState(null);

  const [selectedSubscriptionTierId, setSelectedSubscriptionTierId] = useState(null);
  const [selectedSubscriptionTierError, setSelectedSubscriptionTierError] = useState(null);

  const [customUserLimit, setCustomUserLimit] = useState(null);
  const [customProjectLimit, setCustomProjectLimit] = useState(null);
  const [customCompanyLimit, setCustomCompanyLimit] = useState(null);

  const [resetStartDate, setResetStartDate] = useState(false);

  // data
  const [allSubscriptionTiers, setAllSubscriptionTiers] = useState([]);

  const [administratorIsNewUser, setAdministratorIsNewUser] = useState(false);

  useEffect(() => {
    dataService
      .getAllSubscriptionTiers()
      .then((response) => {
        if (response) {
          setAllSubscriptionTiers(response);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (isEdit) {
      setName(subscriberToEdit.name);
      // setAdministrator to alphabetically sorted subscriberToEdit.administrators
      let sortedAdministrators = subscriberToEdit.administrators.sort((a, b) => a.localeCompare(b));
      setAdministrator(sortedAdministrators);
      setSelectedSubscriptionTierId(subscriberToEdit.subscriptionTierId);
      subscriberToEdit.isCustomCompanyLimit ? setCustomCompanyLimit(subscriberToEdit.companyLimit) : setCustomCompanyLimit(null);
      subscriberToEdit.isCustomProjectLimit ? setCustomProjectLimit(subscriberToEdit.projectLimit) : setCustomProjectLimit(null);
      subscriberToEdit.isCustomUserLimit ? setCustomUserLimit(subscriberToEdit.userLimit) : setCustomUserLimit(null);
    }
  }, [subscriberToEdit]);

  const changeName = (e) => {
    setName(e.target.value);
    if (e.target.value.length < 1) {
      setName(null);
    }
  };

  const changeAdministrator = (e) => {
    setAdministrator(e.target.value);
    if (e.target.value.length < 1) {
      setAdministrator(null);
    }
  };

  const changeAdministratorFirstName = (e) => {
    setAdministratorFirstName(e.target.value);
    if (e.target.value.length < 1) {
      setAdministratorFirstName(null);
    }
  };

  const changeAdministratorLastName = (e) => {
    setAdministratorLastName(e.target.value);
    if (e.target.value.length < 1) {
      setAdministratorLastName(null);
    }
  };

  const changeCustomUserLimit = (limit) => {
    setCustomUserLimit(limit);
    if (limit.length < 1) {
      setCustomUserLimit("");
    }
  };

  const changeCustomProjectLimit = (limit) => {
    setCustomProjectLimit(limit);
    if (limit.length < 1) {
      setCustomProjectLimit("");
    }
  };

  const changeCustomCompanyLimit = (limit) => {
    setCustomCompanyLimit(limit);
    if (limit.length < 1) {
      setCustomCompanyLimit("");
    }
  };

  const getSelectedTierCompanyLimit = () => {
    let tempCompanyLimit = null;

    if (selectedSubscriptionTierId) {
      tempCompanyLimit = allSubscriptionTiers.find((tier) => tier.id === selectedSubscriptionTierId).companyLimit;
    }

    if (tempCompanyLimit === null) {
      return "Unlimited";
    } else {
      return tempCompanyLimit;
    }
  };

  const getSelectedTierProjectLimit = () => {
    let tempProjectLimit = null;

    if (selectedSubscriptionTierId) {
      tempProjectLimit = allSubscriptionTiers.find((tier) => tier.id === selectedSubscriptionTierId).projectLimit;
    }

    if (tempProjectLimit === null) {
      return "Unlimited";
    } else {
      return tempProjectLimit;
    }
  };

  const getSelectedTierUserLimit = () => {
    let tempUserLimit = null;

    if (selectedSubscriptionTierId) {
      tempUserLimit = allSubscriptionTiers.find((tier) => tier.id === selectedSubscriptionTierId).userLimit;
    }

    if (tempUserLimit === null) {
      return "Unlimited";
    } else {
      return tempUserLimit;
    }
  };

  const validateFields = async () => {
    var validationGood = true;

    // Name
    if (name === "" || name === null || name === undefined) {
      validationGood = false;
      setNameError("Required!");
    } else if (name.length > 50) {
      validationGood = false;
      setNameError("Name can't be over 50 characters");
    }

    // For now, we will not allow admin changes to be made to existing subscribers
    if (!isEdit) {
      // Administrator (should be email)
      if (administrator === "" || administrator === null || administrator === undefined) {
        validationGood = false;
        setAdministratorError("Required!");
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(administrator)) {
        validationGood = false;
        setAdministratorError("Administrator must be a valid email address!");
      } else {
        try {
          const response = await dataService.getUserByEmailAddress(administrator);
          if (response) {
            if (response.isPartOfASubscription) {
              validationGood = false;
              setAdministratorError("This user is already part of a subscription!");
            } else if (response.id) {
              setAdministratorIsNewUser(false);
              setAdministratorError(null);
            } else {
              setAdministratorIsNewUser(true);
              setAdministratorError(null);
            }
          } else {
            setAdministratorIsNewUser(true);
            setAdministratorError(null);
          }
        } catch (error) {
          console.log(error);
          validationGood = false;
          setAdministratorError("Error checking email address. Please try again.");
        }
      }

      // First name
      if (administratorFirstName === "" || administratorFirstName === null || administratorFirstName === undefined) {
        validationGood = false;
        setAdministratorFirstNameError("Required!");
      } else if (administratorFirstName.length > 50) {
        validationGood = false;
        setAdministratorFirstNameError("First name can't be over 50 characters");
      }

      // Last name
      if (administratorLastName === "" || administratorLastName === null || administratorLastName === undefined) {
        validationGood = false;
        setAdministratorLastNameError("Required!");
      } else if (administratorLastName.length > 50) {
        validationGood = false;
        setAdministratorLastNameError("Last name can't be over 50 characters");
      }
    }

    // Subscription Tier
    if (selectedSubscriptionTierId === "" || selectedSubscriptionTierId === null || selectedSubscriptionTierId === undefined) {
      validationGood = false;
      setSelectedSubscriptionTierError("Required!");
    }

    return validationGood;
  };

  const handleSubmitSubscriber = async () => {
    resetErrors();
    const isValid = await validateFields();
    let isNewUser = false;

    if (isValid) {
      if (isEdit) {
        let updatedSubscriber = {
          id: subscriberToEdit.id,
          name: name,
          subscriptionTierId: selectedSubscriptionTierId,
          customUserLimit: customUserLimit == "" ? null : parseInt(customUserLimit),
          customProjectLimit: customProjectLimit == "" ? null : parseInt(customProjectLimit),
          customCompanyLimit: customCompanyLimit == "" ? null : parseInt(customCompanyLimit),
          resetStartDate: resetStartDate,
          updatedByUserId: props.userId,
        };

        dataService
          .updateSubscriber(updatedSubscriber)
          .then((response) => {
            if (response.success) {
              props.onSuccess(response.subscriber, crudTypes.UPDATE);
              handleCloseDialog();
            } else {
              setGeneralError(response.message);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        let newSubscriber = {
          name: name,
          administratorEmail: administrator,
          administratorFirstName: administratorFirstName,
          administratorLastName: administratorLastName,
          administratorFirebaseId: null,
          subscriptionTierId: selectedSubscriptionTierId,
          insertedByUserId: props.userId,
          customUserLimit: customUserLimit == "" ? null : parseInt(customUserLimit),
          customProjectLimit: customProjectLimit == "" ? null : parseInt(customProjectLimit),
          customCompanyLimit: customCompanyLimit == "" ? null : parseInt(customCompanyLimit),
        };

        const response = await dataService.getUserByEmailAddress(administrator);
        if (response) {
          if (response.companyIds.length > 0 || response.isSubscriberAdmin) {
            isNewUser = false;
            setGeneralError("This user is already part of a subscription!");
          } else if (response.id) {
            isNewUser = false;
            setGeneralError("This user is already part of a subscription!");
          } else {
            isNewUser = true;
          }
        } else {
          isNewUser = true;
        }

        if (isNewUser) {
          createUserWithEmailAndPassword(auth, administrator, "gX7!vZr@4pQ#dLm9")
            .then((userCredential) => {
              newSubscriber.administratorFirebaseId = userCredential.user.uid;
              sendPasswordResetEmail(auth, administrator)
                .then(() => {
                  dataService
                    .createSubscriber(newSubscriber)
                    .then((response) => {
                      if (response.success) {
                        props.onSuccess(response.subscriber, crudTypes.CREATE);
                        handleCloseDialog();
                      } else {
                        setGeneralError(response.message);
                      }
                    })
                    .catch((error) => {
                      console.log(error);
                    });
                })
                .catch((error) => {
                  console.log(error);
                });
            })
            .catch((error) => {
              console.log(error);
            });
        }
      }
    }
  };

  const handleSuspendSubscription = () => {
    let suspendSubscription = {
      uniqueId: subscriberToEdit.uniqueId,
      updatedByUserId: props.userId,
    };

    dataService
      .suspendSubscriptionBySubscriberUniqueId(suspendSubscription)
      .then((response) => {
        if (response.success) {
          props.onSuccess(response.subscriber, crudTypes.UPDATE);
          handleCloseDialog();
        } else {
          setGeneralError(response.message);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const resetErrors = () => {
    setNameError(null);
    setAdministratorError(null);
    setAdministratorFirstNameError(null);
    setAdministratorLastNameError(null);
    setSelectedSubscriptionTierError(null);
    setGeneralError(null);
  };

  const resetFields = () => {
    setName(null);
    setAdministrator(null);
    setAdministratorFirstName(null);
    setAdministratorLastName(null);
    setSelectedSubscriptionTierId(null);
    setCustomUserLimit(null);
    setCustomProjectLimit(null);
    setCustomCompanyLimit(null);
    setResetStartDate(false);
    setDisclaimer(null);
  };

  const handleCloseDialog = () => {
    setName(null);
    resetErrors();
    resetFields();
    closeDialog();
  };

  const handleSelectSubscriptionTier = (subscriptionTierId) => {
    changeCustomUserLimit("");
    changeCustomProjectLimit("");
    changeCustomCompanyLimit("");

    if (subscriptionTierId === subscriptionTiers.TRYOUT) {
      setDisclaimer("This is a Tryout subscription. The subscription will be active for 90 days. After 90 days, the subscription will no longer be valid.");
    } else {
      setDisclaimer(null);
    }

    if (isEdit && subscriptionTierId !== subscriberToEdit.subscriptionTierId) {
      setResetStartDate(true);
    }

    setSelectedSubscriptionTierId(subscriptionTierId);
  };

  return (
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
          {subscriberToEdit ? "Edit" : "Create"} subscriber
        </DialogTitle>
        <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          {generalError && (
            <div className={classes.errorContainer}>
              <p className={classes.errorMessage}>{generalError}</p>
            </div>
          )}
          <div className={classes.form}>
            <div className={classes.inputContainer}>
              <div className={classes.labelContainer}>
                <label className={classes.labelText}>Subscriber name (company)*</label>
                <p className={classes.inputError}>{nameError}</p>
              </div>
              <TextField
                className={classes.textField}
                type={"text"}
                variant="outlined"
                size="small"
                onChange={(event) => changeName(event)}
                value={name}
                fullWidth={true}
                error={nameError !== null}
                inputProps={{ maxLength: 50 }}
              ></TextField>
            </div>
            {!isEdit && (
              <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Admin first name*</label>
                    <p className={classes.inputError}>{administratorFirstNameError}</p>
                  </div>
                  <TextField
                    className={classes.textField}
                    type={"text"}
                    variant="outlined"
                    size="small"
                    onChange={(event) => changeAdministratorFirstName(event)}
                    value={administratorFirstName}
                    fullWidth={true}
                    error={administratorFirstNameError !== null}
                    inputProps={{ maxLength: 50 }}
                  ></TextField>
                </div>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Admin last name*</label>
                    <p className={classes.inputError}>{administratorLastNameError}</p>
                  </div>
                  <TextField
                    className={classes.textField}
                    type={"text"}
                    variant="outlined"
                    size="small"
                    onChange={(event) => changeAdministratorLastName(event)}
                    value={administratorLastName}
                    fullWidth={true}
                    error={administratorLastNameError !== null}
                    inputProps={{ maxLength: 50 }}
                  ></TextField>
                </div>
              </div>
            )}
            <div className={classes.inputContainer}>
              <div className={classes.labelContainer}>
                <label className={classes.labelText}>Admin email*</label>
                <p className={classes.inputError}>{administratorError}</p>
              </div>
              <TextField
                className={classes.textField}
                type={"text"}
                variant="outlined"
                size="small"
                onChange={(event) => changeAdministrator(event)}
                value={administrator}
                fullWidth={true}
                error={administratorError !== null}
                inputProps={{ maxLength: 50 }}
                disabled={isEdit}
              ></TextField>
            </div>
            {allSubscriptionTiers.length > 1 && (
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>Subscription tier*</label>
                  <p className={classes.inputError}>{selectedSubscriptionTierError}</p>
                </div>

                <Select
                  value={selectedSubscriptionTierId}
                  onChange={(e) => handleSelectSubscriptionTier(e.target.value)}
                  displayEmpty
                  error={selectedSubscriptionTierError}
                  style={{ width: "100%" }}
                  className={classes.textField}
                >
                  {/* If no company is selected, this is a placeholder */}
                  {allSubscriptionTiers.length < 1 && (
                    <MenuItem value="">
                      <em>There are no subscription tiers to choose from</em>
                    </MenuItem>
                  )}
                  {/* Populate the dropdown with companies from allUserCompanies */}
                  {allSubscriptionTiers.map((subscriptionTier, index) => (
                    <MenuItem key={index} value={subscriptionTier.id}>
                      {subscriptionTier.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            )}
            {selectedSubscriptionTierId && (
              <div className={classes.subscriptionLimitContainer}>
                {/* 3 inputfields for every limit of the subscription */}
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>User limit</label>
                    {/* <p className={classes.inputError}>{selectedSubscriptionTierError}</p> */}
                  </div>
                  <TextField
                    className={classes.textField}
                    placeholder={getSelectedTierUserLimit()}
                    type={"number"}
                    variant="outlined"
                    size="small"
                    onChange={(event) => changeCustomUserLimit(event.target.value)}
                    value={customUserLimit}
                    fullWidth={true}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Project limit</label>
                    {/* <p className={classes.inputError}>{selectedSubscriptionTierError}</p> */}
                  </div>
                  <TextField
                    className={classes.textField}
                    placeholder={getSelectedTierProjectLimit()}
                    type={"number"}
                    variant="outlined"
                    size="small"
                    onChange={(event) => changeCustomProjectLimit(event.target.value)}
                    value={customProjectLimit}
                    fullWidth={true}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>Company limit</label>
                    {/* <p className={classes.inputError}>{selectedSubscriptionTierError}</p> */}
                  </div>
                  <TextField
                    className={classes.textField}
                    placeholder={getSelectedTierCompanyLimit()}
                    type={"number"}
                    variant="outlined"
                    size="small"
                    onChange={(event) => changeCustomCompanyLimit(event.target.value)}
                    value={customCompanyLimit}
                    fullWidth={true}
                  />
                </div>
              </div>
            )}
            {isEdit && subscriberToEdit.subscriptionTierId && (
              <div className={classes.inputContainer}>
                <CustomCheckbox checked={resetStartDate} onChange={setResetStartDate} label="Reset start date" />
              </div>
            )}
          </div>
          {disclaimer && (
            <div className={classes.disclaimerContainer}>
              <p className={classes.disclaimerMessage}>{disclaimer}</p>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        {isEdit && (
          <Button variant="destructive" style={{ marginRight: "auto" }} onClick={() => handleSuspendSubscription()}>
            Suspend subscription
          </Button>
        )}
        <Button onClick={() => handleSubmitSubscriber()}>{subscriberToEdit ? "Save changes" : "Create subscriber"}</Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "480px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  disclaimerContainer: {
    width: "100%",
    backgroundColor: "#F8F8F8",

    border: "1px solid #EDEDED",
    borderRadius: "6px",

    marginTop: "16px",
    padding: "12px",
  },

  disclaimerMessage: {
    fontSize: "16px",
    color: "#0C0E29",
  },

  form: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  subscriptionLimitContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  inputContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  inputError: {
    fontSize: "14px",
    color: "#D84949",

    margin: "0",
  },

  textField: {
    height: "40px",
  },

  radioButtonContainer: {
    width: "100%",
    display: "flex",
    gap: "8px",
  },

  radioButton: {
    width: "fit-content",
    height: "40px",

    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",

    padding: "8px",

    borderRadius: "4px",

    transition: "all ease-in-out 0.2s",

    "&:hover": {
      backgroundColor: "#EDEDED",
      cursor: "pointer",
    },
  },

  radio: {
    width: "20px",
    height: "20px",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    boxSizing: "border-box",

    borderRadius: "50%",
    border: "1px solid #0C0E29",
    backgroundColor: "transparent",
  },

  radioIcon: {
    color: "#F8F8F8",
  },

  radioButton__label: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",

    margin: "0",
  },

  saveButton: {
    height: "40px",
    padding: "0 16px",

    alignSelf: "end",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#C3996B",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  deleteButton: {
    height: "40px",
    padding: "0 16px",
    verticalAlign: "center",
    border: "1px solid #D84949",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#D84949",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#C73B3B",
      color: "white",
      border: "1px solid #C73B3B",
      cursor: "pointer",
    },
  },
}));
